import {
  fetchLoyaltyAccounts,
  fetchLoyaltyBenefits,
  redeemLoyaltyBenefit,
} from "../../services/adminApi";


/**
 * Retail POS Loyalty Runtime Adapter.
 *
 * Authority remains the existing Loyalty API and
 * LoyaltyRedemptionEngine.
 *
 * This adapter owns no:
 *
 * - points calculation;
 * - benefit generation;
 * - benefit validation;
 * - redemption rule;
 * - SalesDiscount;
 * - Receipt;
 * - Loyalty persistence.
 */
export function createRetailPOSLoyaltyRuntime({
  loyaltyApi = null,
} = {}) {
  const api =
    loyaltyApi
    ?? {
      fetchLoyaltyAccounts,
      fetchLoyaltyBenefits,
      redeemLoyaltyBenefit,
    };

  assertLoyaltyApi(
    api
  );

  return Object.freeze({
    async resolveLoyalty(
      customer,
      context = {}
    ) {
      const companyUuid =
        requireCompanyUuid(
          context
        );

      const clientUuid =
        resolveCustomerUuid(
          customer
        );

      const [
        accountsResponse,
        benefitsResponse,
      ] = await Promise.all([
        api.fetchLoyaltyAccounts(
          {
            client_uuid:
              clientUuid,
          },
          {
            companyUuid,
          }
        ),

        api.fetchLoyaltyBenefits(
          {
            client_uuid:
              clientUuid,

            status:
              "available",
          },
          {
            companyUuid,
          }
        ),
      ]);

      const accounts =
        normalizeCollection(
          accountsResponse,
          "accounts"
        );

      const benefits =
        normalizeCollection(
          benefitsResponse,
          "benefits"
        );

      return Object.freeze({
        account:
          accounts[0]
          ?? null,

        accounts,

        benefits,
      });
    },


    async redeemBenefit(
      receipt,
      benefit,
      context = {}
    ) {
      const companyUuid =
        requireCompanyUuid(
          context
        );

      const receiptUuid =
        resolveUuid(
          receipt,
          "Receipt"
        );

      const benefitUuid =
        resolveUuid(
          benefit,
          "Loyalty benefit"
        );

      const response =
        await api.redeemLoyaltyBenefit(
          benefitUuid,
          receiptUuid,
          {
            companyUuid,
          }
        );

      return unwrapResponseData(
        response
      );
    },
  });
}


export function isRetailPOSLoyaltyRuntime(
  runtime
) {
  return Boolean(
    runtime
    && typeof runtime
      === "object"
    && typeof runtime.resolveLoyalty
      === "function"
    && typeof runtime.redeemBenefit
      === "function"
  );
}


function resolveCustomerUuid(
  customer
) {
  const raw =
    typeof customer
      === "string"
      ? customer
      : (
          customer?.uuid
          ?? customer?.client_uuid
        );

  const value =
    typeof raw === "string"
      ? raw.trim()
      : "";

  if (!value) {
    throw new TypeError(
      "Customer must expose a non-empty uuid."
    );
  }

  return value;
}


function resolveUuid(
  reference,
  label
) {
  const raw =
    typeof reference
      === "string"
      ? reference
      : (
          reference?.uuid
          ?? reference?.receipt_uuid
          ?? reference?.benefit_uuid
        );

  const value =
    typeof raw === "string"
      ? raw.trim()
      : "";

  if (!value) {
    throw new TypeError(
      `${label} must expose a non-empty uuid.`
    );
  }

  return value;
}


function normalizeCollection(
  response,
  field
) {
  const payload =
    unwrapResponseData(
      response
    );

  if (Array.isArray(payload)) {
    return payload;
  }

  if (
    payload
    && Array.isArray(
      payload[field]
    )
  ) {
    return payload[field];
  }

  return [];
}


function requireCompanyUuid(
  context
) {
  const companyUuid =
    typeof context?.companyUuid
      === "string"
      ? context.companyUuid.trim()
      : "";

  if (!companyUuid) {
    throw new TypeError(
      "companyUuid must be a non-empty string."
    );
  }

  return companyUuid;
}


function unwrapResponseData(
  response
) {
  if (
    response
    && typeof response
      === "object"
    && Object.prototype
      .hasOwnProperty.call(
        response,
        "data"
      )
  ) {
    return response.data;
  }

  return response;
}


function assertLoyaltyApi(
  api
) {
  [
    "fetchLoyaltyAccounts",
    "fetchLoyaltyBenefits",
    "redeemLoyaltyBenefit",
  ].forEach(
    (method) => {
      if (
        !api
        || typeof api[method]
          !== "function"
      ) {
        throw new TypeError(
          `loyaltyApi must expose ${method}().`
        );
      }
    }
  );
}
